import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Text,
  Title,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import type { TourConfig } from "@pulse-ui/widget-sdk";

interface TourOverlayProps {
  instanceId: string;
  config: TourConfig;
  onClose: () => void;
}

const GAP = 8;
const CARD_WIDTH = 320;

export function TourOverlay({ instanceId, config, onClose }: TourOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [cardHeight, setCardHeight] = useState(160);
  const cardRef = useRef<HTMLDivElement>(null);
  const step = config.steps[stepIndex];

  // Body copy length varies per step, so the card's real height must be measured —
  // a fixed assumed height lets long bodies push the Back/Next row off-screen.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const updateHeight = () => setCardHeight(el.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [step]);

  useEffect(() => {
    setStepIndex(0);
  }, [config]);

  useEffect(() => {
    if (!step) {
      setRect(null);
      return;
    }

    const scope = document.querySelector(`[data-tour-scope="${instanceId}"]`);
    const target = scope?.querySelector(
      `[data-tour-target="${step.targetId}"]`,
    );
    const updateRect = () => setRect(target?.getBoundingClientRect() ?? null);

    target?.scrollIntoView({ block: "nearest", inline: "nearest" });
    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    const observer = target ? new ResizeObserver(updateRect) : null;
    if (target) observer?.observe(target);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
      observer?.disconnect();
    };
  }, [instanceId, step]);

  const cardPosition = useMemo(() => {
    const margin = 16;
    if (!rect) {
      return {
        top: "50%" as const,
        left: "50%" as const,
        transform: "translate(-50%, -50%)",
      };
    }
    const maxTop = Math.max(margin, window.innerHeight - cardHeight - margin);
    const top = Math.min(rect.bottom + GAP, maxTop);
    const left = Math.min(
      Math.max(rect.left, margin),
      window.innerWidth - CARD_WIDTH - margin,
    );
    return { top, left, transform: undefined };
  }, [rect, cardHeight]);

  if (!step) return null;

  const isLast = stepIndex === config.steps.length - 1;

  return createPortal(
    <Box style={{ position: "fixed", inset: 0, zIndex: 10000 }}>
      {rect ? (
        <Box
          style={{
            position: "fixed",
            top: rect.top - GAP,
            left: rect.left - GAP,
            width: rect.width + GAP * 2,
            height: rect.height + GAP * 2,
            borderRadius: 6,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.6)",
            pointerEvents: "none",
            transition:
              "top 150ms ease, left 150ms ease, width 150ms ease, height 150ms ease",
          }}
        />
      ) : (
        <Box
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
          }}
        />
      )}
      <Paper
        ref={cardRef}
        shadow="lg"
        radius="md"
        p="md"
        withBorder
        style={{
          position: "fixed",
          top: cardPosition.top,
          left: cardPosition.left,
          transform: cardPosition.transform,
          width: CARD_WIDTH,
          zIndex: 10001,
        }}
      >
        <Group justify="space-between" mb="xs" wrap="nowrap">
          <Title order={6}>{step.title}</Title>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onClose}
            aria-label="Close tour"
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          {step.body}
        </Text>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {stepIndex + 1} / {config.steps.length}
          </Text>
          <Group gap="xs">
            {stepIndex > 0 && (
              <Button
                size="xs"
                variant="default"
                onClick={() => setStepIndex((i) => i - 1)}
              >
                Back
              </Button>
            )}
            <Button
              size="xs"
              onClick={() => (isLast ? onClose() : setStepIndex((i) => i + 1))}
            >
              {isLast ? "Done" : "Next"}
            </Button>
          </Group>
        </Group>
      </Paper>
    </Box>,
    document.body,
  );
}
